<script setup lang="ts">
import BoostItemGrid from './BoostItemGrid.vue';
import type { User } from '~/types/user';
import UhInput from '~/components/forms/UhInput.vue';
import { useMainStore } from '~/stores/mainStore';
import { useCheckoutStore } from '@checkout/stores';

const { user }: { user: User } = useAuth();
const mainStore = useMainStore();
const checkoutStore = useCheckoutStore();

const fixedBoostError = ref<string>('');

const clearFixedBoostError = () => {
  fixedBoostError.value = '';
};

// Watch for changes to validate
watch(() => checkoutStore.fixedBoostsCount, (newValue) => {
  const num = Number(newValue);
  if (newValue !== '' && newValue !== null && (isNaN(num) || num < 1)) {
    fixedBoostError.value = 'Please enter at least 1 boost';
  } else {
    fixedBoostError.value = '';
  }
});

</script>

<template>
  <div class="space-y-6">
    <div>
      <UhHeading lvl="5">Give your listing a little boost!</UhHeading>
      <p>
        Boosting pushes your properties to the top of the list. Get more
        enquiries by reaching more students.
      </p>
    </div>

    <UhHR class="my-0" />

    <div>
      <p>
        You currently have
        <span class="font-bold">
          {{ (user.landlord.boosts ?? 0).toLocaleString() }} Boosts
        </span>
        available.
      </p>
    </div>

    <UhHR class="my-0" />

    <div
      v-if="!mainStore.siteSettings.fixedRateBoostEnabled"
      class="flex w-full flex-col"
    >
      <UhHeading lvl="5">Buy more Boosts</UhHeading>

      <BoostItemGrid class="mb-2 mt-4" />

      <div class="inline-flex justify-end">
        <label class="text-sm text-gray-500">
          All prices are excluding VAT
        </label>
      </div>
    </div>
    <div
      v-if="mainStore.siteSettings.fixedRateBoostEnabled"
      class="flex w-full flex-col space-y-4"
    >
      <div>
        <UhHeading lvl="5">Buy Boosts</UhHeading>
        <p class="mt-2 text-sm text-gray-600">
          {{ mainStore.siteSettings.fixedBoostPrice?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }) }} per boost (excluding VAT)
        </p>
      </div>

      <UhInput
        v-model="checkoutStore.fixedBoostsCount"
        data-label="fixedBoostsCount"
        name="fixedBoostsCount"
        label="Number of boosts"
        type="number"
        required
        placeholder="Enter quantity"
        min="1"
        :error="fixedBoostError"
        @input="clearFixedBoostError"
      />

      <div class="inline-flex justify-end">
        <label class="text-sm text-gray-500">
          All prices are excluding VAT
        </label>
      </div>
    </div>
  </div>
</template>
